import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Music } from 'lucide-react'

export const Route = createFileRoute('/app/song-review')({
  component: SongReviewPage,
})

function SongReviewPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to review songs</div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <SongSelectionDashboard userId={currentUser._id} />
    </ErrorBoundary>
  )
}

function SongSelectionDashboard({ userId }: { userId: string }) {
  const songsForReview = useConvexQuery(api.flashcards.getSongForReview, {
    userId: userId as any,
    limit: 20,
  })

  if (!songsForReview) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading songs...</div>
      </div>
    )
  }

  if (songsForReview.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="terminal-border border-trail-tan p-8 text-center">
            <h1 className="text-3xl font-bold text-trail-tan mb-4 font-mono tracking-wider">
              [NO SONGS DUE FOR REVIEW]
            </h1>
            <p className="text-trail-tan font-mono text-lg mb-4">
              All your songs are caught up! No flashcards grouped by song are due right now.
            </p>
            <p className="text-sky-blue font-mono text-sm mb-6">
              Try the individual flashcard review mode or check back later.
            </p>
            <Link
              to="/app/flashcards"
              className="inline-flex items-center gap-2 px-6 py-3 bg-terminal-green/10 border-2 border-terminal-green text-terminal-green hover:bg-terminal-green hover:text-black transition-colors font-mono font-bold"
            >
              Review Individual Flashcards →
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 terminal-border p-6">
          <h1 className="text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [SONG REVIEW MODE]
          </h1>
          <p className="text-trail-tan font-mono text-sm">
            Review flashcards grouped by song. Listen to the full song while reviewing all related cards.
          </p>
        </div>

        {/* Song Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {songsForReview.map((song) => (
            <Link
              key={song.songId}
              to="/app/song-review/$songId"
              params={{ songId: song.songId }}
              className="terminal-border border-sky-blue p-6 hover:bg-sky-blue/10 transition-colors group"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-terminal-green/10 border-2 border-terminal-green group-hover:bg-terminal-green group-hover:text-black transition-colors">
                  <Music className="w-8 h-8 text-terminal-green group-hover:text-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-terminal-green mb-1 font-mono truncate">
                    {song.songTitle}
                  </h2>
                  {song.songArtist && (
                    <p className="text-trail-tan font-mono text-sm mb-2 truncate">
                      {song.songArtist}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-sm font-mono">
                    <span className="text-sky-blue">
                      {song.flashcardCount} cards
                    </span>
                    {song.averageEaseFactor && (
                      <span className="text-terminal-green">
                        EF: {song.averageEaseFactor.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-xs font-mono text-trail-tan">
                    Due: {new Date(song.earliestDueDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Help Text */}
        <div className="mt-6 terminal-border border-trail-tan p-4 bg-black/50">
          <p className="text-trail-tan font-mono text-sm">
            <strong className="text-sky-blue">[TIP]</strong> Song review mode plays the full song and automatically advances through flashcards at their timestamp positions. Perfect for learning lyrics in context!
          </p>
        </div>
      </div>
    </div>
  )
}
